// client/audio — secret: Web Audio synthesis (no external assets), sound bank.
// Procedural per-call: cheap and zero-dep.

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;

function ensure(): { ctx: AudioContext; master: GainNode } {
  if (!ctx || !master) {
    const Cls = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new Cls();
    master = ctx.createGain();
    master.gain.value = 0.55;
    master.connect(ctx.destination);
  }
  return { ctx, master };
}

// Some browsers block AudioContext until user gesture. Resume on first interaction.
function attemptResume(): void {
  if (ctx && ctx.state === 'suspended') {
    void ctx.resume();
  }
}

if (typeof window !== 'undefined') {
  const onGesture = (): void => {
    ensure();
    attemptResume();
  };
  window.addEventListener('pointerdown', onGesture, { once: false, passive: true });
  window.addEventListener('keydown', onGesture, { once: false, passive: true });
}

export interface PlayOpts {
  volume?: number;
  loop?: boolean;
}

function tone(opts: {
  freq: number;
  freqEnd?: number;
  durationMs: number;
  type?: OscillatorType;
  attackMs?: number;
  releaseMs?: number;
  gain?: number;
  detune?: number;
}): void {
  if (muted) return;
  const { ctx: c, master: m } = ensure();
  const now = c.currentTime;
  const osc = c.createOscillator();
  osc.type = opts.type ?? 'sine';
  osc.frequency.value = opts.freq;
  if (opts.detune) osc.detune.value = opts.detune;
  if (opts.freqEnd != null) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(20, opts.freqEnd),
      now + opts.durationMs / 1000
    );
  }
  const g = c.createGain();
  const peak = opts.gain ?? 0.4;
  const a = (opts.attackMs ?? 5) / 1000;
  const r = (opts.releaseMs ?? 80) / 1000;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(peak, now + a);
  g.gain.linearRampToValueAtTime(0, now + opts.durationMs / 1000 + r);
  osc.connect(g);
  g.connect(m);
  osc.start(now);
  osc.stop(now + opts.durationMs / 1000 + r + 0.05);
}

function noise(opts: {
  durationMs: number;
  filterFreq: number;
  filterQ?: number;
  attackMs?: number;
  releaseMs?: number;
  gain?: number;
  filterSweepTo?: number;
}): void {
  if (muted) return;
  const { ctx: c, master: m } = ensure();
  const now = c.currentTime;
  const dur = opts.durationMs / 1000;
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * (dur + 0.05)), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = opts.filterFreq;
  filter.Q.value = opts.filterQ ?? 1;
  if (opts.filterSweepTo != null) {
    filter.frequency.linearRampToValueAtTime(opts.filterSweepTo, now + dur);
  }
  const g = c.createGain();
  const peak = opts.gain ?? 0.35;
  const a = (opts.attackMs ?? 4) / 1000;
  const r = (opts.releaseMs ?? 60) / 1000;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(peak, now + a);
  g.gain.linearRampToValueAtTime(0, now + dur + r);
  src.connect(filter);
  filter.connect(g);
  g.connect(m);
  src.start(now);
  src.stop(now + dur + r + 0.05);
}

const BANK = {
  swordSwing(): void {
    noise({ durationMs: 110, filterFreq: 3500, filterSweepTo: 800, gain: 0.22, attackMs: 2, releaseMs: 30 });
  },
  swordHit(): void {
    tone({ freq: 220, freqEnd: 90, durationMs: 80, type: 'square', gain: 0.28, attackMs: 1, releaseMs: 60 });
    noise({ durationMs: 70, filterFreq: 1200, filterQ: 4, gain: 0.18, releaseMs: 80 });
  },
  swordCrit(): void {
    tone({ freq: 360, freqEnd: 120, durationMs: 110, type: 'sawtooth', gain: 0.32, attackMs: 1, releaseMs: 90 });
    tone({ freq: 800, freqEnd: 1400, durationMs: 90, type: 'triangle', gain: 0.18, attackMs: 1, releaseMs: 60 });
  },
  wolfBite(): void {
    tone({ freq: 180, freqEnd: 95, durationMs: 90, type: 'sawtooth', gain: 0.22, attackMs: 1, releaseMs: 60 });
    noise({ durationMs: 60, filterFreq: 1500, gain: 0.16 });
  },
  wolfDeath(): void {
    tone({ freq: 420, freqEnd: 110, durationMs: 480, type: 'sawtooth', gain: 0.3, attackMs: 8, releaseMs: 120 });
  },
  playerHurt(): void {
    tone({ freq: 260, freqEnd: 160, durationMs: 200, type: 'square', gain: 0.22, attackMs: 4, releaseMs: 120 });
  },
  playerDeath(): void {
    tone({ freq: 220, freqEnd: 60, durationMs: 800, type: 'sawtooth', gain: 0.34, attackMs: 10, releaseMs: 200 });
  },
  loot(): void {
    tone({ freq: 880, freqEnd: 1320, durationMs: 110, type: 'triangle', gain: 0.18, attackMs: 2, releaseMs: 80 });
    tone({ freq: 1320, freqEnd: 1760, durationMs: 90, type: 'sine', gain: 0.14, attackMs: 30, releaseMs: 80 });
  },
} as const;

export type SoundId = keyof typeof BANK;

export function play(id: SoundId, _opts: PlayOpts = {}): void {
  const fn = BANK[id];
  if (!fn) return;
  try {
    fn();
  } catch {
    // swallow — audio failures should never break the game
  }
}

export function setMuted(v: boolean): void {
  muted = v;
}

export function loadBank(_name: string): Promise<void> {
  return Promise.resolve();
}
